import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import JellyfishLoader from './JellyfishLoader';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check if we have the necessary tokens or if this is a recovery/signup type
    const accessToken = searchParams.get('access_token');
    const refreshToken = searchParams.get('refresh_token');
    const type = searchParams.get('type');
    const setup = searchParams.get('setup');
    
    // If we have tokens, set up session with them
    if (accessToken && refreshToken) {
      const setupSession = async () => {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Session setup error:', error);
            setMessage('Invalid or expired reset link. Please request a new password reset.');
            return;
          }

          if (!data.session || !data.user) {
            setMessage('Invalid session. Please request a new password reset.');
            return;
          }

          console.log('Session established for password reset:', data.user.email);
          setMessage('Please enter your new password below.');
        } catch (error) {
          console.error('Error setting up session:', error);
          setMessage('Error setting up password reset. Please try again.');
        }
      };

      setupSession();
    } else if (type === 'recovery') {
      // For recovery type without tokens, check if user is already authenticated
      const checkAuth = async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Session check error:', error);
            setMessage('Please sign in first or request a new password reset.');
            return;
          }

          if (session && session.user) {
            console.log('User already authenticated for password reset:', session.user.email);
            setMessage('Please enter your new password below.');
          } else {
            setMessage('Please sign in first or request a new password reset.');
          }
        } catch (error) {
          console.error('Error checking session:', error);
          setMessage('Error checking authentication. Please try again.');
        }
      };

      checkAuth();
    } else if (type === 'signup' && setup === 'true') {
      // For signup password setup, check if user is already authenticated
      const checkAuth = async () => {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Session check error:', error);
            setMessage('Please complete the signup process first.');
            return;
          }

          if (session && session.user) {
            console.log('User authenticated for password setup:', session.user.email);
            setMessage('Please set your password to complete your account setup.');
          } else {
            setMessage('Please complete the signup process first.');
          }
        } catch (error) {
          console.error('Error checking session:', error);
          setMessage('Error checking authentication. Please try again.');
        }
      };

      checkAuth();
    } else {
      setMessage('Invalid reset link. Please request a new password reset.');
    }

    // Check for dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    if (savedDarkMode) {
      setIsDarkMode(true);
      document.body.classList.add('dark-mode');
    }

    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [searchParams]);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    if (newDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  };

  // Match signup rules: 8+ chars, upper, lower, number, 2 special (so reset cannot be weaker)
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    const specialCount = (password.match(/[!@#$%^&*(),.?":{}|<>]/g) || []).length;
    if (specialCount < 2) {
      return 'Password must contain at least two special characters (!@#$%^&*(),.?":{}|<>).';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    // Validate passwords
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setMessage(passwordError);
      setIsLoading(false);
      return;
    }

    try {
      // Update password using Supabase
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('Password update error:', error);
        setMessage(getUserFacingErrorMessage(error, 'Failed to update password. Please try again.'));
        setIsLoading(false);
        return;
      }

      console.log('Password updated successfully');
      setIsSuccess(true);
      setMessage('Password updated successfully! Redirecting to dashboard...');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Unexpected error updating password:', error);
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="reset-password">
        <div className="reset-password-container">
          <div className="reset-password-content">
            <div className="success-icon">✅</div>
            <h1>Password Updated!</h1>
            <p>Your password has been successfully updated. Redirecting to dashboard...</p>
            <div className="loading-spinner">
              <JellyfishLoader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <button 
            className="dark-mode-toggle"
            onClick={toggleDarkMode}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
        </div>
        
        <div className="reset-password-content">
          <div className="reset-password-icon">🔒</div>
          <h1>Reset Your Password</h1>
          <p className="reset-message">{message}</p>
          
          <form onSubmit={handleSubmit} className="reset-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                disabled={isLoading}
                className={message && !isSuccess ? 'error' : ''}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                disabled={isLoading}
                className={message && !isSuccess ? 'error' : ''}
              />
            </div>
            
            <button 
              type="submit" 
              className="reset-button"
              disabled={isLoading || !password || !confirmPassword}
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
          
          <div className="password-requirements">
            <h3>Password Requirements:</h3>
            <ul>
              <li>At least 8 characters long</li>
              <li>Contains at least one uppercase letter</li>
              <li>Contains at least one lowercase letter</li>
              <li>Contains at least one number</li>
              <li>Contains at least two special characters (!@#$%^&*(),.?":&#123;&#125;|&lt;&gt;)</li>
            </ul>
          </div>
          
          <div className="back-to-signin">
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
